import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiRequestConfig } from 'api/v1/api';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

export type UserNotification = {
  id: number;
  notification_type: string;
  title: string;
  body: string;
  is_read: boolean;
  created_at: string;
  payload?: Record<string, unknown>;
};

export type NotificationsResponse = {
  items: UserNotification[];
  unread_count: number;
};

const QUERY_KEY = ['notifications'];

export const useNotifications = (enabled = true) => {
  const { data: session } = useAuthSession();

  return useQuery<NotificationsResponse>({
    queryKey: QUERY_KEY,
    queryFn: () =>
      api
        .get('/notifications', { skipGlobalErrorHandler: true } as ApiRequestConfig)
        .then((response) => response.data),
    enabled: enabled && isAuthSessionActive(session),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) =>
      api.post(`/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};
